import clx from 'classnames';
import { type CSSProperties, type ReactNode } from 'react';
import useDimensions from 'react-cool-dimensions';
import styled from 'styled-components';

export interface ICardGroupLayoutProps {
  vertical?: boolean;
  /**
   * Dispone le card in griglia a N colonne di pari larghezza.
   * Ignorata se `vertical` è attiva; sotto HORIZONTAL_BREAKPOINT collassa
   * comunque a verticale, come il layout orizzontale.
   */
  gridColumns?: number;
  children: (vertical: boolean) => ReactNode;
  /**
   * Rimuove lo spazio riservato per le etichette dei campi.
   * Da utilizzare se il primo FormItem ha le etichette nascoste.
   */
  noLabelOffset?: boolean;
}

const HORIZONTAL_BREAKPOINT = 600;

const CardGroupWrapper = styled.div`
  padding-top: 10px;

  &.no-label-offset {
    padding-top: 0;
  }

  .ant-radio-group {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
    gap: 16px;
  }

  &.vertical .ant-radio-group {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  &.grid .ant-radio-group {
    display: grid;
    grid-template-columns: repeat(var(--card-group-columns), minmax(0, 1fr));
  }
`;

/**
 * Wrapper layout per card group (radio o checkbox).
 *
 * Gestisce la disposizione delle card (orizzontale, verticale o griglia a
 * `gridColumns` colonne) e il responsive: sotto HORIZONTAL_BREAKPOINT i layout
 * orizzontale e griglia vengono forzati a verticale in base alla larghezza
 * effettiva del container (react-cool-dimensions).
 *
 * Il valore effettivo di vertical viene esposto ai children tramite render prop,
 * così i componenti interni (es. aria-orientation) restano allineati al layout reale.
 *
 * Riutilizzabile per RadioCardGroup e futuro CheckboxCardGroup.
 */
export function CardGroupLayout({
  vertical = false,
  noLabelOffset,
  gridColumns,
  children,
}: Readonly<ICardGroupLayoutProps>) {
  const { observe, width } = useDimensions<HTMLDivElement>();
  const isNarrow = width > 0 && width < HORIZONTAL_BREAKPOINT;
  const effectiveVertical = vertical || isNarrow;
  const isGrid = gridColumns !== undefined && !effectiveVertical;

  return (
    <CardGroupWrapper
      ref={observe}
      className={clx({
        vertical: effectiveVertical,
        grid: isGrid,
        'no-label-offset': noLabelOffset,
      })}
      style={
        isGrid
          ? ({ '--card-group-columns': gridColumns } as CSSProperties)
          : undefined
      }
    >
      {children(effectiveVertical)}
    </CardGroupWrapper>
  );
}
